import { BALL_VALUES, type PlayerIndex, type Visit } from '@aiscoreboard/contracts';
import { BALL_GLOSS, BALL_SWATCH, ballKey } from '../lib/balls';
import { useT } from '../lib/i18n';

interface Props {
  visits: Visit[];
  player: PlayerIndex;
}

/**
 * What this player has actually done in the frame: every ball potted, in
 * order, with each finished break totalled where it ended.
 *
 * A running score says somebody made 15; this says red, black, red, pink,
 * which is what anybody watching wants to know and what lets an operator spot
 * a mis-tap while it is still the last thing they did. Play resumes after the
 * total, so a frame reads left to right as it was played.
 *
 * The chips are lit like the pot buttons and the strip holds exactly four
 * rows. It used to reserve room for a maximum break plus a wall of fouls,
 * which is a shape that has never appeared on a table — so most of the frame
 * it was empty space taken from the two things anybody actually looks at, the
 * name and the score. Four rows is a real break's worth; anything past it
 * scrolls, which is the cheaper way to pay for the frame that fills it.
 *
 * The space is reserved, but nothing is drawn around it. The chips are
 * already round, coloured and outlined; a panel of their own on top of that
 * put a third border inside a panel that has two, and the box was visible for
 * the whole frame while the thing it framed was visible for part of it. The
 * balls sit straight on the panel now and the reservation is invisible until
 * a ball arrives to occupy it.
 */
export function BreakHistory({ visits, player }: Props) {
  const mine = visits.filter((v) => v.player === player);

  return (
    <div
      data-break-log
      className="flex h-full max-h-full w-full flex-wrap content-start items-start gap-1
                 overflow-y-auto px-0.5 py-1"
    >
      {mine.map((visit, i) => (
        <VisitRun key={i} visit={visit} />
      ))}
    </div>
  );
}

function VisitRun({ visit }: { visit: Visit }) {
  const t = useT();

  return (
    <>
      {visit.entries.map((entry, i) =>
        entry.kind === 'pot' ? (
          <span
            key={i}
            title={
              entry.freeBall
                ? t('match.freeBallWorth', { n: entry.points })
                : `${t(ballKey(entry.color))}${entry.count > 1 ? ` x${entry.count}` : ''}`
            }
            style={BALL_GLOSS}
            className={[
              'grid h-[1.4rem] shrink-0 place-items-center rounded-full text-[0.6rem] font-bold',
              'text-white',
              entry.count > 1 ? 'w-auto px-1.5' : 'w-[1.4rem]',
              BALL_SWATCH[entry.color],
              // A nominated ball scored the ball *on*, not itself, so the chip
              // has to carry the figure or the log reads as a mis-tap: a black
              // in the run and only one point on the board. The gold ring is
              // the same one the award wears everywhere else.
              entry.freeBall ? 'ring-1 ring-sys-yellow/80' : '',
            ].join(' ')}
          >
            {entry.freeBall
              ? entry.points
              : entry.count > 1
                ? `${entry.count}x${BALL_VALUES[entry.color]}`
                : ''}
          </span>
        ) : entry.kind === 'points' ? (
          <span
            key={i}
            data-added-points
            title={t('scoreEntry.record', { n: entry.points })}
            className="grid h-[1.4rem] shrink-0 place-items-center rounded-md bg-white/15 px-1.5 text-[0.7rem] font-bold tabular-nums text-white"
          >
            {entry.points}
          </span>
        ) : (
          <span
            key={i}
            className="grid h-[1.4rem] shrink-0 place-items-center rounded-full bg-ball-red/90 px-1.5
                       text-[0.6rem] font-bold uppercase text-white"
          >
            {t('match.foul', { n: entry.points })}
          </span>
        ),
      )}

      {/* The total lands where the break ended, so the next visit reads as a
          continuation rather than as part of the same run. */}
      {visit.ended && visit.points > 0 && !(visit.entries.length === 1 && visit.entries[0]?.kind === 'points') && (
        <span className="mr-1 grid h-[1.4rem] shrink-0 place-items-center rounded-full bg-white/85
                         px-1.5 text-[0.65rem] font-black text-hud-bg">
          {visit.points}
        </span>
      )}
    </>
  );
}
